import { faToggleOff, faToggleOn } from '@fortawesome/pro-solid-svg-icons';
import { ApiService, type DomainSummary } from '@supply-mover-2-0/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { TemplatePreview } from '../../components/display/TemplatePreview';
import { Button } from '../../components/forms/Button';
import { RichTextEditor } from '../../components/forms/RichTextEditor';
import { SelectSearch } from '../../components/forms/SelectSearch';
import type { VariableMappingVariable } from '../../components/forms/VariableMapping';
import { VariableMapping } from '../../components/forms/VariableMapping';
import { Modal } from '../../components/overlays/Modal';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Toast } from '../../services/ToastService';
import { convertDataSourcesToVariables } from '../utils/dataSourceToVariables';
import { DynamicFieldValue } from './DynamicFieldValue';
import { EmailFromField } from './EmailFromField';

// Fields of the /email/templates records this component reads.
interface EmailTemplateRecord {
	id: number | string;
	name: string;
	description?: string;
	template: string;
}

interface EmailStepConfigProps {
	config: any;
	setConfig: (config: any) => void;
	getAvailableDataSources: () => any[];
	getDisplayText: (value: any) => string;
	verifiedDomains: Array<DomainSummary>;
	domainsLoading: boolean;
	fallbackFrom?: string;
}

export function EmailStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
	verifiedDomains,
	domainsLoading,
	fallbackFrom,
}: EmailStepConfigProps) {
	const { t } = useTranslation();
	const colors = useThemeColors();

	// Track intended mode - initialize from config.emailMode if it exists, otherwise infer from templateId
	const [intendedMode, setIntendedMode] = useState<'manual' | 'template'>(
		config.emailMode || (config.templateId ? 'template' : 'manual'),
	);
	const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
	const [templatePreview, setTemplatePreview] = useState<string>('');
	const [loadingPreview, setLoadingPreview] = useState(false);
	const [showVariableMapping, setShowVariableMapping] = useState(false);
	const [showBodyVariableMapping, setShowBodyVariableMapping] = useState(false);

	// Load template on mount if templateId exists
	useEffect(() => {
		if (config.templateId && !selectedTemplate) {
			loadTemplateDetails(String(config.templateId));
		}
	}, [config.templateId]);

	// Update intended mode when config.emailMode changes (e.g., when modal reopens)
	useEffect(() => {
		if (config.emailMode) {
			setIntendedMode(config.emailMode);
		}
	}, [config.emailMode]);

	// Load recent templates
	const loadRecentTemplates = useCallback(async () => {
		try {
			const response = await ApiService.apiGet<{
				templates: Array<unknown>;
			}>('/email/templates', {
				page: 1,
				limit: 10,
			});
			const templates = response.templates.map((template: any) => ({
				value: template.id.toString(),
				label: template.name,
				subtitle: template.description,
			}));

			// If we have a selected template that's not in recent, fetch and add it
			if (
				config.templateId &&
				!templates.find((t: any) => t.value === String(config.templateId))
			) {
				try {
					const selectedTemplateData =
						await ApiService.apiGet<EmailTemplateRecord>(
							`/email/templates/${config.templateId}`,
						);
					templates.unshift({
						value: selectedTemplateData.id.toString(),
						label: selectedTemplateData.name,
						subtitle: selectedTemplateData.description,
					});
				} catch (error) {
					console.debug(
						'Could not load selected template for recent list:',
						error,
					);
				}
			}

			return templates;
		} catch (error) {
			console.error('Failed to load recent templates:', error);
			return [];
		}
	}, [config.templateId]);

	// Search templates
	const searchTemplates = useCallback(async (query: string) => {
		try {
			const response = await ApiService.apiGet<{
				templates: Array<unknown>;
			}>('/email/templates', {
				page: 1,
				limit: 20,
				search: query,
			});
			return response.templates.map((template: any) => ({
				value: template.id.toString(),
				label: template.name,
				subtitle: template.description,
			}));
		} catch (error) {
			console.error('Failed to search templates:', error);
			return [];
		}
	}, []);

	// Load template details and preview
	const loadTemplateDetails = async (templateId: string) => {
		try {
			setLoadingPreview(true);
			const template = await ApiService.apiGet<EmailTemplateRecord>(
				`/email/templates/${templateId}`,
			);
			setSelectedTemplate(template);

			// Generate preview
			try {
				const previewResponse = await ApiService.apiPost(
					'/email/templates/test',
					{
						template: template.template,
					},
				);
				setTemplatePreview(previewResponse?.rendered || template.template);
			} catch (error) {
				// Fallback to raw template if preview fails
				setTemplatePreview(template.template);
			}
		} catch (error) {
			console.error('Failed to load template details:', error);
			Toast.show({
				type: 'error',
				text1: t('Failed to load template'),
			});
		} finally {
			setLoadingPreview(false);
		}
	};

	// Handle template selection
	const handleTemplateSelect = (templateId: string) => {
		setConfig({
			...config,
			emailMode: 'template',
			templateId: templateId ? parseInt(templateId) : undefined,
		});
		if (templateId) {
			loadTemplateDetails(templateId);
		} else {
			setSelectedTemplate(null);
			setTemplatePreview('');
		}
	};

	// Handle variable mapping save
	const handleSaveVariableMapping = (mapping: Record<string, string>) => {
		setConfig({
			...config,
			templateVariables: mapping,
		});
		setShowVariableMapping(false);
	};

	// Get template variables for mapping
	const getTemplateVariables = (): VariableMappingVariable[] => {
		if (!selectedTemplate?.variables) {
			return [];
		}
		return selectedTemplate.variables.map((v: any) => ({
			name: v.name,
			type: v.type,
			required: v.required,
			description: v.description,
			defaultValue: v.defaultValue,
		}));
	};

	// No auto-mapping - user defines their own variables

	// Convert available data sources to context-aware variables for the merge variable picker
	// This enables proper handling of arrays (with loop syntax) and objects (with block syntax)
	const contextAwareVariables = useMemo(() => {
		const dataSources = getAvailableDataSources();

		return convertDataSourcesToVariables(dataSources);
	}, [getAvailableDataSources]);

	// Get variables from the mapping (not from body content)
	// These are user-defined variable names
	const getBodyVariablesForMapping =
		useCallback((): VariableMappingVariable[] => {
			const mapping = config.bodyVariableMapping || {};

			return Object.keys(mapping).map((varName) => ({
				name: varName,
				type: 'string',
				required: false,
				description: `Custom variable for email body`,
			}));
		}, [config.bodyVariableMapping]);

	// Handle saving body variable mapping
	const handleSaveBodyVariableMapping = useCallback(
		(mapping: Record<string, string>) => {
			setConfig({
				...config,
				bodyVariableMapping: mapping,
			});
			setShowBodyVariableMapping(false);
		},
		[config, setConfig],
	);

	return (
		<div className="space-y-6">
			{/* To Field (always required) */}
			<DynamicFieldValue
				label={t('To (Recipients)')}
				value={config.to || ''}
				onChange={(value) => {
					setConfig({ ...config, to: value });
				}}
				dataType="email"
				placeholder={t('Enter email address or select from data')}
				required={true}
				description={t(
					'Email recipient address. Use dynamic values to send to different people based on execution data.',
				)}
				availableData={getAvailableDataSources()}
				displayTransform={getDisplayText}
			/>

			{/* From Field */}
			<EmailFromField
				label={t('From (Sender)')}
				value={config.from || ''}
				onChange={(value) => {
					setConfig({ ...config, from: value });
				}}
				description={t(
					'Select a verified domain. Leave empty to use primary domain.',
				)}
				availableData={getAvailableDataSources()}
				verifiedDomains={verifiedDomains}
				domainsLoading={domainsLoading}
				fallbackFrom={fallbackFrom}
			/>

			{/* Subject Field */}
			<DynamicFieldValue
				label={t('Subject')}
				value={config.subject || ''}
				onChange={(value) => {
					setConfig({ ...config, subject: value });
				}}
				dataType="string"
				placeholder={t('Enter email subject')}
				required={true}
				description={t(
					'Email subject line. Can include dynamic content from execution data.',
				)}
				availableData={getAvailableDataSources()}
				displayTransform={getDisplayText}
			/>

			{/* Email Body Section */}
			<div>
				<div className="flex items-center justify-between mb-3">
					<label
						className={`text-sm font-medium ${colors.classes.textPrimary}`}
					>
						{t('Email Body')}
					</label>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							const newMode = intendedMode === 'manual' ? 'template' : 'manual';
							setIntendedMode(newMode);
							setConfig({
								...config,
								emailMode: newMode,
							});
						}}
						className="flex items-center gap-2"
						title={
							intendedMode === 'template'
								? t('Switch to Manual Input')
								: t('Switch to Use Template')
						}
					>
						<Icon
							icon={intendedMode === 'template' ? faToggleOn : faToggleOff}
							className={
								intendedMode === 'template'
									? colors.classes.statusSuccess
									: colors.classes.textSecondary
							}
							size={16}
						/>
						<span className="text-xs font-medium">
							{intendedMode === 'template'
								? t('Use Template')
								: t('Manual Input')}
						</span>
					</Button>
				</div>

				{/* Body Content based on mode */}
				<div className="mt-4">
					{intendedMode === 'template' ? (
						<>
							{/* Template Selection with SelectSearch */}
							<div className="mb-4">
								<SelectSearch
									mode="dynamic"
									value={config.templateId?.toString()}
									onChange={handleTemplateSelect}
									placeholder={t('Search templates...')}
									loadRecent={loadRecentTemplates}
									searchItems={searchTemplates}
									recentGroupLabel={t('Recent Templates')}
									searchGroupLabel={t('Search Results')}
									emptyRecentMessage={t('No recent templates')}
									emptySearchMessage={t('No templates found')}
									searchDebounceMs={300}
									minSearchLength={1}
									fullWidth
								/>
							</div>

							{/* Template Preview */}
							{selectedTemplate && (
								<div>
									<div className="flex justify-between items-center mb-2">
										<label
											className={`text-sm font-medium ${colors.classes.textSecondary}`}
										>
											{t('Template Preview')}
										</label>
										{selectedTemplate.variables &&
											selectedTemplate.variables.length > 0 && (
												<Button
													size="xs"
													variant="outline"
													onClick={() => {
														setShowVariableMapping(true);
													}}
												>
													{t('Map Variables')} (
													{selectedTemplate.variables.length})
												</Button>
											)}
									</div>
									<TemplatePreview
										content={templatePreview}
										loading={loadingPreview}
										maxHeight="300px"
									/>
								</div>
							)}

							{/* Variable Mapping Modal */}
							{showVariableMapping && selectedTemplate && (
								<Modal
									isOpen={showVariableMapping}
									onClose={() => {
										setShowVariableMapping(false);
									}}
									title={t('Map Template Variables')}
								>
									<VariableMapping
										variables={getTemplateVariables()}
										mapping={config.templateVariables || {}}
										onSave={handleSaveVariableMapping}
										onCancel={() => {
											setShowVariableMapping(false);
										}}
										useDynamicFieldValue={true}
										availableData={getAvailableDataSources()}
										displayTransform={getDisplayText}
									/>
								</Modal>
							)}
						</>
					) : (
						<>
							{/* Manual Body Field with WYSIWYG Editor */}
							<div>
								<div className="flex justify-between items-center mb-2">
									<label
										className={`text-sm font-medium ${colors.classes.textSecondary}`}
									>
										{t('Body Content')}
									</label>
									<Button
										size="xs"
										variant="outline"
										onClick={() => {
											setShowBodyVariableMapping(true);
										}}
									>
										{t('Manage Variables')}
										{Object.keys(config.bodyVariableMapping || {}).length > 0 &&
											` (${Object.keys(config.bodyVariableMapping || {}).length})`}
									</Button>
								</div>
								<RichTextEditor
									value={config.body || ''}
									onChange={(value) => {
										setConfig({ ...config, body: value });
									}}
									placeholder={t('Enter email body content...')}
									minHeight="300px"
									contextAwareVariables={contextAwareVariables}
								/>
								<p className={`text-sm mt-2 ${colors.classes.textSecondary}`}>
									{t(
										'Use the {} button in the toolbar to insert flow variables, or type {{variableName}} directly',
									)}
								</p>
							</div>

							{/* Body Variable Mapping Modal */}
							{showBodyVariableMapping && (
								<Modal
									isOpen={showBodyVariableMapping}
									onClose={() => {
										setShowBodyVariableMapping(false);
									}}
									title={t('Manage Body Variables')}
								>
									<VariableMapping
										variables={getBodyVariablesForMapping()}
										mapping={config.bodyVariableMapping || {}}
										onSave={handleSaveBodyVariableMapping}
										onCancel={() => {
											setShowBodyVariableMapping(false);
										}}
										useDynamicFieldValue={true}
										availableData={getAvailableDataSources()}
										displayTransform={getDisplayText}
										enableAutoMap={false}
										allowEditVariableNames={true}
										description="Define custom variables for your email body. Each variable name you create can be used in the body as {{variableName}}."
									/>
								</Modal>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
